/**
 * サイドバーコンポーネント
 * レスポンシブ・ダークモード対応
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Stack,
  NavLink,
  ScrollArea,
  Text,
  Group,
  Divider,
  ActionIcon,
  Badge,
  Select,
  LoadingOverlay,
  useMantineColorScheme,
  Drawer,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconBuildingStore,
  IconExternalLink,
} from '@tabler/icons-react';
import { useAuth } from '../../auth/contexts/AuthContext';
import { navigationItems, filterNavigationByRole } from '../../../shared/config/navigation';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

interface SidebarProps {
  opened: boolean;
  mobileOpened?: boolean;
  onMobileClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ opened, mobileOpened = false, onMobileClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(user?.store_id?.toString() || null);
  const [isChangingStore, setIsChangingStore] = useState(false);

  // スクロール位置保持用のref（デスクトップ・モバイル別々）
  const desktopScrollViewportRef = useRef<HTMLDivElement>(null);
  const mobileScrollViewportRef = useRef<HTMLDivElement>(null);
  const SCROLL_POSITION_KEY_DESKTOP = 'sidebar-scroll-position-desktop';
  const SCROLL_POSITION_KEY_MOBILE = 'sidebar-scroll-position-mobile';

  // 店舗一覧を取得（管理者のみ）
  const { data: stores } = useQuery({
    queryKey: ['stores'],
    queryFn: async () => {
      const response = await axios.get('/stores');
      return response.data.data;
    },
    enabled: user?.role === 'admin',
  });

  // 店舗切り替え処理
  const changeStore = async (storeId: string) => {
    setIsChangingStore(true);
    try {
      await axios.post('/v2/auth/change-store', { store_id: parseInt(storeId) });

      notifications.show({
        title: '店舗切り替え完了',
        message: '店舗を切り替えました',
        color: 'green',
      });

      // ページ全体をリロードして確実にリフレッシュ
      window.location.reload();
    } catch (error) {
      notifications.show({
        title: 'エラー',
        message: '店舗の切り替えに失敗しました',
        color: 'red',
      });
      setSelectedStoreId(user?.store_id?.toString() || null);
      setIsChangingStore(false);
    }
  };



  // ロール・店舗IDに基づいてフィルタリング
  const filteredItems = React.useMemo(() => {
    return filterNavigationByRole(navigationItems, user?.role, user?.store_id ?? undefined);
  }, [user?.role, user?.store_id]);

  const toggleExpanded = (label: string) => {
    setExpandedItems((prev) =>
      prev.includes(label)
        ? prev.filter((item) => item !== label)
        : [...prev, label]
    );
  };

  const renderMenuItem = (item: any, depth = 0) => {
    if (item.divider) {
      return <Divider key={`divider-${depth}-${Math.random()}`} my="xs" />;
    }

    const Icon = item.icon;
    const isActive = location.pathname === item.link || 
                    (item.children && item.children.some((child: any) => location.pathname === child.link));
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.label);

    if (hasChildren) {
      return (
        <Box key={item.label}>
          <NavLink
            label={
              <Group justify="space-between">
                <Text size="sm">{item.label}</Text>
                {item.badge && (
                  typeof item.badge === 'string' || typeof item.badge === 'number' ? (
                    <Badge size="sm" color={item.badgeColor || 'red'} circle>
                      {item.badge}
                    </Badge>
                  ) : (
                    item.badge
                  )
                )}
              </Group>
            }
            leftSection={Icon && <Icon size={18} />}
            onClick={() => {
              toggleExpanded(item.label);
            }}
            active={isActive}
            variant="subtle"
            styles={{
              root: {
                borderRadius: 8,
                marginBottom: 4,
                paddingLeft: depth > 0 ? 40 : undefined,
              },
            }}
          />
          {isExpanded && (
            <Box pl={28}>
              {item.children.map((child: any) => renderMenuItem(child, depth + 1))}
            </Box>
          )}
        </Box>
      );
    }

    return (
      <NavLink
        key={item.label}
        label={
          <Group justify="space-between">
            <Text size="sm">{item.label}</Text>
            <Group gap={4}>
              {item.openInNewWindow && (
                <IconExternalLink size={14} style={{ opacity: 0.5 }} />
              )}
              {item.badge && (
                typeof item.badge === 'string' || typeof item.badge === 'number' ? (
                  <Badge size="sm" color={item.badgeColor || 'red'} circle>
                    {item.badge}
                  </Badge>
                ) : (
                  item.badge
                )
              )}
            </Group>
          </Group>
        }
        leftSection={depth === 0 && Icon ? <Icon size={18} /> : undefined}
        onClick={() => {
          if (item.link) {
            if (item.openInNewWindow) {
              // 新しいウィンドウで開く
              window.open(item.link, '_blank', 'noopener,noreferrer');
            } else {
              navigate(item.link);
            }
            // モバイルドロワーを閉じる
            onMobileClose?.();
          }
        }}
        active={isActive}
        variant="subtle"
        styles={{
          root: {
            borderRadius: 8,
            marginBottom: 4,
            paddingLeft: depth > 0 ? 12 : undefined,
            boxShadow:'calc(0.0625rem * var(--mantine-scale)) 0 calc(0.1875rem * var(--mantine-scale)) rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) calc(2.25rem * var(--mantine-scale)) 0 calc(1.75rem * var(--mantine-scale)) calc(-0.4375rem * var(--mantine-scale)), rgba(0, 0, 0, 0.04) calc(1.0625rem * var(--mantine-scale)) 0 calc(1.0625rem * var(--mantine-scale)) calc(-0.4375rem * var(--mantine-scale));',
          },
        }}
      />
    );
  };

  // サイドバーコンテンツ
  const SidebarContent = ({ 
    viewportRef, 
    storageKey 
  }: { 
    viewportRef: React.RefObject<HTMLDivElement>;
    storageKey: string;
  }) => {
    // スクロール位置変更時のハンドラ
    const handleScrollPositionChange = ({ y }: { x: number; y: number }) => {
      sessionStorage.setItem(storageKey, y.toString());
    };

    // 初期スクロール位置を復元
    useEffect(() => {
      if (viewportRef.current) {
        const saved = sessionStorage.getItem(storageKey);
        if (saved) {
          const position = parseInt(saved, 10);
          // 少し遅延させてDOMが完全にレンダリングされてから実行
          requestAnimationFrame(() => {
            if (viewportRef.current) {
              viewportRef.current.scrollTop = position;
            }
          });
        }
      }
    }, [storageKey, viewportRef]);

    return (
    <Stack h="100%" gap={0}>
      {/* ロゴエリア */}
      <Box p="sm">
        <Group gap="xs">
          <img
            src="/public/images/s-logo.png" 
            alt="Suien Links Logo"
            style={{
              width: 32,
              height: 32,
              borderRadius: 4,
              filter: isDark 
                ? 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.3))' 
                : 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.5))',
              objectFit: 'contain',
            }}
          />
          <Box>
            <Text
              size="md"
              fw={700}
              style={{
                background: isDark 
                  ? 'linear-gradient(135deg, #4dabf7 0%, #339af0 100%)'
                  : 'linear-gradient(135deg, #1e90ff 0%, #00bfff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '0.5px',
              }}
            >
              Suien Links -水縁-
            </Text>
          </Box>
        </Group>
      </Box>

      {/* メニューエリア */}
      <ScrollArea 
        flex={1} 
        p="sm" 
        viewportRef={viewportRef}
        onScrollPositionChange={handleScrollPositionChange}
        scrollbarSize={8}
      >
        <Stack gap={4}>
          {filteredItems.map((item) => renderMenuItem(item))}
        </Stack>
      </ScrollArea>

      {/* 店舗情報エリア */}
      <Box 
        p="sm" 
        style={{ 
          borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : '#e0e0e0'}`,
          position: 'relative' 
        }}
      >
        <LoadingOverlay visible={isChangingStore} />
        {user?.role === 'admin' ? (
          <Stack gap="xs">
            <Group gap="xs">
              <ActionIcon variant="light" size="lg" radius="md">
                <IconBuildingStore size={18} />
              </ActionIcon>
              <Text size="xs" c="dimmed">店舗を選択</Text>
            </Group>
            <Select
              size="sm"
              value={selectedStoreId}
              onChange={(value) => {
                if (value) {
                  setSelectedStoreId(value);
                  changeStore(value);
                }
              }}
              data={
                stores?.map((store: any) => ({
                  value: store.id.toString(),
                  label: store.name,
                })) || []
              }
              placeholder="店舗を選択"
              searchable={false}
              allowDeselect={false}
              nothingFoundMessage="店舗が見つかりません"
            />
          </Stack>
        ) : (
          <Group gap="xs">
            <ActionIcon variant="light" size="lg" radius="md">
              <IconBuildingStore size={18} />
            </ActionIcon>
            <Box flex={1}>
              <Text size="xs" c="dimmed">現在の店舗</Text>
              <Text size="sm" fw={500}>
                {user?.store?.name || '店舗未選択'}
              </Text>
            </Box>
          </Group>
        )}
      </Box>
    </Stack>
    );
  };

  return (
    <>
      {/* デスクトップ用サイドバー */}
      <Box
        w={opened ? 240 : 0}
        h="100vh"
        display={{ base: 'none', md: 'block' }}
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          background: isDark
            ? 'linear-gradient(180deg, rgba(26, 27, 30, 1) 0%, rgba(15, 23, 42, 1) 100%)'
            : 'linear-gradient(180deg, #ffffff 0%, #f0f9ff 100%)',
          borderRight: `1px solid ${isDark
            ? 'rgba(255, 255, 255, 0.1)'
            : '#e0e0e0'}`,
          boxShadow: '4px 0 20px rgba(0, 0, 0, 0.1), 8px 0 40px rgba(0, 0, 0, 0.05)',
          transition: 'width 0.3s ease',
          overflow: 'hidden',
          zIndex: 100,
        }}
      >
        <SidebarContent viewportRef={desktopScrollViewportRef} storageKey={SCROLL_POSITION_KEY_DESKTOP} />
      </Box>

      {/* モバイル用ドロワー */}
      <Drawer
        opened={mobileOpened}
        onClose={() => onMobileClose?.()}
        size="240px"
        padding="0"
        withCloseButton={false}
        overlayProps={{
          backgroundOpacity: 0.5,
          blur: 16,
        }}
        styles={{
          body: {
            background: isDark
              ? 'linear-gradient(180deg, rgba(26, 27, 30, 1) 0%, rgba(15, 23, 42, 1) 100%)'
              : 'linear-gradient(180deg, #ffffff 0%, #f0f9ff 100%)',
          },
        }}
      >
        <SidebarContent viewportRef={mobileScrollViewportRef} storageKey={SCROLL_POSITION_KEY_MOBILE} />
      </Drawer>
    </>
  );
};